import { FunctionComponent, useState } from 'react'
import { AppSkeleton, AppImage, AppRow, AppCol, AppPopover, AppTooltip } from '@/shared/ui'
import { ProductComment } from '../product-comment/product-comment'
import { ChatCircleIcon, ArrowsClockwiseIcon, ArrowCounterClockwiseIcon } from '@phosphor-icons/react'
import { XCircleIcon, CaretDownIcon } from '@phosphor-icons/react'
import { RejectItemModal } from '../reject-item/reject-item-modal'
import { Product } from './product-types'
import { APC_MODE } from '../../enumerations/admin-product-card.enumeration'

type AdminProductCardLabels = {
  [key: string]: string
}

/**
 * Label texts.
 */
const LABELS: AdminProductCardLabels = {
  unitValue: 'Unit value',
  optional: 'Optional',
  moreInfo: 'More',
  itemDetails: 'Product attributes',
  addComment: 'Add comment',
  rejectItem: 'Reject product',
  unitsToReject: 'Units to reject',
  comment: 'Comment',
  writeCommentHere: 'Write comment here',
  save: 'Save',
  cancel: 'Cancel',
  reject: 'Reject',
  specificQuantity: 'Specific quantity',
  all: 'All',
  youAreRejecting: 'You are rejecting',
  undoRejectItem: 'Undo reject item'
}

export type AdminProductCardProps = {
  showCommentForm?: boolean
  allowAddComment?: boolean
  loading?: boolean
  imageSize?: string
  product: Product
  mode?: APC_MODE
  slot?: JSX.Element
  slotWidth?: string
  direction?: string
  showRejectItem: boolean
  showUndo: boolean
  itemV1?: any
  className?: string
  sendComment?: (comment: string) => void
  cancelComment?: () => void
  onReject?: (value: number) => void
  onUndoRejection?: () => void
  copy?: (text: string) => string
}

export const AdminProductCard: FunctionComponent<AdminProductCardProps> = ({
  loading = false,
  product = {},
  showCommentForm = true,
  allowAddComment = true,
  imageSize,
  mode = APC_MODE.ADD,
  slot,
  showRejectItem = false,
  showUndo = false,
  className = '',
  sendComment = () => {},
  cancelComment = () => {},
  onReject = () => {},
  onUndoRejection = () => {},
  copy = (text: string): string => (LABELS[text] ? LABELS[text] : text)
}: AdminProductCardProps): JSX.Element => {
  const [showDetails, setShowDetails] = useState(false)
  const [rejectModalVisible, setRejectModalVisible] = useState(false)

  const {
    image = '',
    name = 'Product name long long name',
    pum = '2 und | Price: $33.180',
    price = {},
    instructions,
    contentUnits,
    attributes: details = {},
    addedBy,
    replaceTo,
    replacedBy,
    unitValueText,
    quantity = 0,
    um
  } = product

  const { sticker, stickerColor } = price

  const {
    image: replacedByImage = '',
    title: replacedByTitle = '',
    contentUnits: repleacedByContentUnits = ''
  } = replacedBy ?? {}

  /**
   * Draw product attributes.
   */
  const getDetails = () => {
    const attributes: any[] = []
    Object.keys(details).forEach((key, idx) => {
      attributes.push(
        <div key={idx} className={`gungnir-apc-table-details-row ${idx % 2 === 0 ? 'white' : 'gray'}`}>
          <span className='capitalize'>{key}</span>
          <span>{details[key]}</span>
        </div>
      )
    })
    return attributes
  }

  const reject = (value: number): void => {
    setRejectModalVisible(false)
    onReject(value)
  }

  return (
    <AppSkeleton loading={loading} className={`gungnir-apc-skeleton ${className}`}>
      <div className={`gungnir-apc ${className}`}>
        <AppRow gutter={12} wrap={false}>
          <AppCol className='gungnir-apc-image-container' flex={imageSize}>
            <AppImage
              className='gungnir-apc-image'
              src={image}
              preview={false}
              fallback='data:image/png;base64,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'
            />
            {sticker ? <div className='gungnir-apc-sticker'>{sticker}</div> : null}
          </AppCol>
          <AppCol flex='auto'>
            <AppRow wrap={false} gutter={12} justify='space-between'>
              <AppCol className='gungnir-apc-data-container' flex='auto'>
                <div className='gungnir-apc-product-name'>
                  {name}
                  {contentUnits && <span> · {contentUnits}</span>}
                </div>
                <div className='gungnir-apc-product-price'>{pum}</div>
                {instructions ? (
                  <AppPopover
                    overlayClassName='gungnir-apc-instructions-popover'
                    placement='bottom'
                    content={instructions}
                    trigger='hover'
                  >
                    <div className='gungnir-apc-instructions'>
                      <ChatCircleIcon /> <span>{instructions}</span>
                    </div>
                  </AppPopover>
                ) : (
                  allowAddComment && (
                    <ProductComment
                      className='gungnir-apc-comment-form'
                      show={showCommentForm}
                      mode={mode}
                      sendComment={sendComment}
                      cancelComment={cancelComment}
                      copy={copy}
                    />
                  )
                )}
              </AppCol>
              {showRejectItem || slot ? (
                <AppCol>
                  <AppRow gutter={[0, 24]}>
                    {showRejectItem ? (
                      <AppCol span={24} className='gungnir-apc-close-container'>
                        <div className='gungnir-apc-close'>
                          <AppTooltip title={copy('rejectItem')} placement='bottomRight'>
                            <XCircleIcon onClick={() => setRejectModalVisible(true)} />
                          </AppTooltip>
                        </div>
                      </AppCol>
                    ) : null}
                    {showUndo ? (
                      <AppCol span={24} className='gungnir-apc-close-container'>
                        <div className='gungnir-apc-close'>
                          <AppTooltip title={copy('undoRejectItem')} placement='bottomRight'>
                            <div>
                              <ArrowCounterClockwiseIcon onClick={onUndoRejection} />
                            </div>
                          </AppTooltip>
                        </div>
                      </AppCol>
                    ) : null}
                    {slot ? (
                      <AppCol span={24} className='gungnir-apc-slot-container'>
                        {slot}
                      </AppCol>
                    ) : null}
                  </AppRow>
                </AppCol>
              ) : null}
            </AppRow>
            {details && Object.keys(details).length ? (
              <AppRow gutter={12}>
                <AppCol flex='auto'>
                  <div className='gungnir-apc-product-details'>
                    <div className='gungnir-apc-pd-title'>
                      <a onClick={() => setShowDetails(!showDetails)}>
                        {copy('moreInfo')} <CaretDownIcon rotate={showDetails ? 180 : 0} />
                      </a>
                    </div>
                    {showDetails ? (
                      <>
                        {unitValueText ? (
                          <div className='gungnir-apc-pd-item'>
                            <b>{copy('unitValue')}:</b> <span>{unitValueText}</span>
                          </div>
                        ) : (
                          ''
                        )}
                        {addedBy && (
                          <div className='gungnir-apc-pd-item'>
                            <b>{copy('addedBy')}:</b> {addedBy.icon} <span>{addedBy.text}</span>
                          </div>
                        )}
                        {replaceTo && (
                          <div className='gungnir-apc-pd-item'>
                            <b>{copy('replaceTo')}:</b> <a onClick={replaceTo.onClick}>{replaceTo.text}</a>
                          </div>
                        )}
                        {replacedBy && (
                          <div className='gungnir-apc-pd-item'>
                            <div>
                              <b>{copy('replacedBy')}:</b>
                            </div>
                            <div className='gungnir-apc-pd-replaced-by-wrapper'>
                              <div className='gungnir-apc-replaced-by-container'>
                                <AppImage
                                  className='gungnir-apc-replaced-by'
                                  alt={replacedByTitle}
                                  src={replacedByImage}
                                  preview={false}
                                  fallback='data:image/png;base64,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'
                                />
                                <ArrowsClockwiseIcon className='gungnir-apc-replaced-sticker' />
                              </div>
                              <div className='gungnir-apc-pd-replaced-by-content'>
                                {`${replacedByTitle} · ${repleacedByContentUnits}`}
                              </div>
                            </div>
                          </div>
                        )}
                        {Object.keys(details).length > 0 ? (
                          <div className='gungnir-apc-pd-table'>
                            <p className='gungnir-apc-pd-table-title-label'>{copy('itemDetails')}</p>
                            <div className='gungnir-apc-pd-table-details-body'>{getDetails()}</div>
                          </div>
                        ) : (
                          ''
                        )}
                      </>
                    ) : null}
                  </div>
                </AppCol>
              </AppRow>
            ) : null}
          </AppCol>
        </AppRow>
        <RejectItemModal
          visible={rejectModalVisible}
          title={copy('unitsToReject')}
          copy={copy}
          product={{ name, quantity, um, contentUnits }}
          onCancel={() => {
            setRejectModalVisible(false)
          }}
          onReject={reject}
        />
      </div>
    </AppSkeleton>
  )
}
